let audioContext = null;

const getAudioContext = () => {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) {
    return null;
  }

  if (!audioContext) {
    audioContext = new AudioContextClass();
  }

  if (audioContext.state === "suspended") {
    audioContext.resume();
  }

  return audioContext;
};

// A "pluck" = a clean fundamental tone plus a much quieter, faster-decaying
// high overtone that mimics the brief mallet/attack transient of a struck
// marimba bar - this is what gives a plucked/percussive note its clarity,
// versus a plain oscillator which just sounds like a flat synth beep.
const playPluck = (ctx, frequency, startTime, duration, peakGain) => {
  const body = ctx.createOscillator();
  const bodyGain = ctx.createGain();
  body.type = "sine";
  body.frequency.setValueAtTime(frequency, startTime);
  bodyGain.gain.setValueAtTime(0, startTime);
  bodyGain.gain.linearRampToValueAtTime(peakGain, startTime + 0.004);
  bodyGain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
  body.connect(bodyGain);
  bodyGain.connect(ctx.destination);
  body.start(startTime);
  body.stop(startTime + duration);

  const clickDuration = duration * 0.3;
  const click = ctx.createOscillator();
  const clickGain = ctx.createGain();
  click.type = "sine";
  click.frequency.setValueAtTime(frequency * 2.76, startTime); // inharmonic overtone, like wood/marimba
  clickGain.gain.setValueAtTime(0, startTime);
  clickGain.gain.linearRampToValueAtTime(peakGain * 0.3, startTime + 0.002);
  clickGain.gain.exponentialRampToValueAtTime(0.001, startTime + clickDuration);
  click.connect(clickGain);
  clickGain.connect(ctx.destination);
  click.start(startTime);
  click.stop(startTime + clickDuration);
};

// Approximates the Google Pixel "Eureka" notification sound (Android 12+
// default, replacing "Popcorn"): two rapid, bright, high, crisp/digital
// notes, descending A♭6 -> E♭6. The exact Pixel audio is a proprietary
// Google sound file, not something we can bundle/reproduce exactly - this
// is a synthesized approximation using its actual musical notes.
const EUREKA_STYLE_TUNE = [
  { freq: 1661.22, duration: 0.12 }, // Ab6 (G#6)
  { freq: 1244.51, duration: 0.2 }, // Eb6 (D#6)
];
const NOTE_GAP = 0.01;
const PEAK_GAIN = 0.8;

/**
 * Plays a short, soft marimba-style "pluck" chime (Pixel "Eureka" style)
 * using the Web Audio API, so it doesn't depend on bundling/loading an audio
 * file. Plays once per new order - no looping/repeats.
 *
 * Browsers block audio until the user has interacted with the page at least
 * once, so the very first attempt right after page load may be silently
 * ignored - this is expected browser behavior, not a bug.
 */
export const playNewOrderSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    let t = ctx.currentTime;
    EUREKA_STYLE_TUNE.forEach(({ freq, duration }) => {
      playPluck(ctx, freq, t, duration, PEAK_GAIN);
      t += duration + NOTE_GAP;
    });
  } catch (err) {
    console.warn("Unable to play notification sound:", err);
  }
};

/**
 * Compares the current list of orders against the previously seen order IDs
 * (tracked in a ref by the caller) and plays a sound if any brand-new order
 * id has appeared. The first call just seeds the "known" set without playing
 * a sound, so existing orders on initial page load don't trigger it.
 *
 * @param {{ id: number|string }[]} orders
 * @param {{ current: Set<number|string>|null }} knownOrderIdsRef
 * @returns {{ isInitial: boolean, newOrders: object[] }} newly appeared orders
 */
export const notifyOnNewOrders = (orders, knownOrderIdsRef) => {
  const list = orders || [];
  const currentIds = new Set(list.map((order) => order.id));

  if (knownOrderIdsRef.current === null) {
    knownOrderIdsRef.current = currentIds;
    return { isInitial: true, newOrders: [] };
  }

  const newOrders = list.filter(
    (order) => !knownOrderIdsRef.current.has(order.id)
  );

  if (newOrders.length > 0) {
    playNewOrderSound();
  }

  knownOrderIdsRef.current = currentIds;
  return { isInitial: false, newOrders };
};
